import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { drawingActions } from './../../state/stores/drawings';
import { WinnersLink, WinnersModal } from './winners';
import { isObjectEmpty } from './../../utils';

import mrMonopoly from '../../images/winners-mr-monopoly.png';

import styles from './drawings.module.scss';

const formattedDrawDate = (date, drawingDate = false, subtractADay = false) => {
    const fiveYearsOut = dayjs().add(5, 'year');
    const day = dayjs(date);
    if (day.isAfter(fiveYearsOut)) {
        return 'TBD';
    } else {
        if (drawingDate) {
            // as in VA the desire is that the final draw date render
            // the same day as the penultimate drawing. SCBZ Admin doesn't
            // presently (~March 2020) support mutiple draw dates on the same day
            // so we are compensating for that by subtracting a day from
            // the category 2 drawing date(s)
            return !subtractADay
                ? day.format('MMMM D, YYYY')
                : day.subtract(1, 'day').format('MMMM D, YYYY');
        } else {
            return day.format(`MMMM D, YYYY<br />h:mm:ss a`);
        }
    }
};

export const Prize = ({ number = '' }) => {
    return !(parseInt(number) === 11) ? (
        <>
            1 $10,000 Prize
            <br />
            40 $500 Prizes
        </>
    ) : (
        <>
            1 $10,000 a Month For Life
            <br />
            Grand Prize
        </>
    );
};

Prize.propTypes = {
    category: PropTypes.string.isRequired,
};

export const DrawingsTable = () => {
    const dispatch = useDispatch();
    const drawings = useSelector(({ drawings }) => drawings.drawings);
    const [winnersDrawing, toggleWinners] = useState({});

    useEffect(() => {
        if (drawings.length === 0) {
            drawingActions(dispatch).getDrawings();
        }
    }, [drawings, dispatch]);

    const promotionDrawings = useMemo(
        () => drawings.filter(({ game_id }) => game_id === '1574'),
        [drawings]
    );

    return (
        <>
            <img src={mrMonopoly} className="mr-monopoly" alt="Mr Monopoly" />

            <div className="drawings-table">
                {promotionDrawings.length ? (
                    <table>
                        <thead>
                            <tr>
                                <th>Drawing</th>
                                <th>Entry Start Date</th>
                                <th>Entry Deadline Date</th>
                                <th>Drawing Date</th>
                                <th>Prizes</th>
                                <th>Winners</th>
                            </tr>
                        </thead>
                        <tbody className={styles.drawingsBody}>
                            {promotionDrawings.map(draw => (
                                <tr key={draw.id}>
                                    <td data-title="Drawing">
                                        {parseInt(draw.draw_number) === 11
                                            ? 'FINAL DRAWING/11'
                                            : draw.draw_number}
                                    </td>
                                    {/* START DATE */}
                                    {parseInt(draw.draw_number) === 11 ? (
                                        <td data-title="Entry Start Date">
                                            FEBRUARY 16, 2021
                                            <br />
                                            12:00:00 AM
                                        </td>
                                    ) : (
                                        <td
                                            data-title="Entry Start Date"
                                            dangerouslySetInnerHTML={{
                                                __html: formattedDrawDate(
                                                    draw.start_date
                                                ),
                                            }}
                                        />
                                    )}
                                    {/* DEADLINE DATE */}
                                    {parseInt(draw.draw_number) === 10 ? (
                                        <td data-title="Entry Deadline Date">
                                            APRIL 10, 2022
                                            <br />
                                            11:59:59 PM
                                        </td>
                                    ) : parseInt(draw.draw_number) === 11 ? (
                                        <td data-title="Entry Deadline Date">
                                            NOVEMBER 13, 2022
                                            <br />
                                            11:59:59 PM
                                        </td>
                                    ) : (
                                        <td
                                            data-title="Entry Deadline Date"
                                            dangerouslySetInnerHTML={{
                                                __html: formattedDrawDate(
                                                    draw.end_date
                                                ),
                                            }}
                                        />
                                    )}
                                    {/* DRAWING DATE */}
                                    {parseInt(draw.draw_number) === 10 ? (
                                        <td data-title="Drawing Date">
                                            APRIL 12, 2022
                                        </td>
                                    ) : parseInt(draw.draw_number) === 11 ? (
                                        <td data-title="Drawing Date">
                                            NOVEMBER 15, 2022
                                        </td>
                                    ) : (
                                        <td
                                            data-title="Drawing Date"
                                            dangerouslySetInnerHTML={{
                                                __html: formattedDrawDate(
                                                    draw.draw_date,
                                                    true
                                                ),
                                            }}
                                        />
                                    )}
                                    {/* PRIZES */}
                                    {parseInt(draw.draw_number) === 11 ? (
                                        <td data-title="Prizes">
                                            1 $10,000 a Month for Life
                                            <br />
                                            Grand Prize
                                        </td>
                                    ) : (
                                        <td data-title="Prizes">
                                            <Prize number={draw.draw_number} />
                                        </td>
                                    )}
                                    <td data-title="Winners">
                                        <WinnersLink
                                            drawing={draw}
                                            clickHandler={drawing => {
                                                toggleWinners(drawing);
                                            }}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="no-drawings">Loading...</div>
                )}
            </div>
            <WinnersModal
                category={winnersDrawing.category}
                draw_number={winnersDrawing.draw_number}
                showModal={!isObjectEmpty(winnersDrawing)}
                finalDraw={parseInt(winnersDrawing.category, 10) !== 1}
                closeModal={() => toggleWinners({})}
            />
        </>
    );
};

export default DrawingsTable;
